/*
  ShotMapCard — post-round shot-tracker visualisation.
  ---------------------------------------------------------------------
  Renders on the round-summary page and shows:

    • A "Your shots today" summary: total shots logged, holes with shots,
      average driver + average 7-iron distances.
    • A per-club distance breakdown table (sorted longest → shortest,
      shows count + avg + longest).
    • A hole-by-hole shot list with each shot's club + yards travelled.

  Kept intentionally free of Leaflet / Google Maps — golfers are already
  using the round-map hero during play. This card is about STATS. If we
  ever want a coloured shot polyline overlay, we'd add it here as a
  separate "View shot map" pill that links into a full-screen view.
*/
import { Golf, TrendUp } from "@phosphor-icons/react";

const CLUB_ORDER = [
  "driver", "3w", "5w", "7w", "hybrid",
  "2i", "3i", "4i", "5i", "6i", "7i", "8i", "9i",
  "pw", "gw", "sw", "lw", "putter",
];

const CLUB_LABELS = {
  driver: "Driver", "3w": "3-wood", "5w": "5-wood", "7w": "7-wood", hybrid: "Hybrid",
  "2i": "2-iron", "3i": "3-iron", "4i": "4-iron", "5i": "5-iron", "6i": "6-iron",
  "7i": "7-iron", "8i": "8-iron", "9i": "9-iron",
  pw: "PW", gw: "GW", sw: "SW", lw: "LW", putter: "Putter",
};

export default function ShotMapCard({ shots = [] }) {
  if (!shots || shots.length === 0) return null;

  // Group by hole for the per-hole list.
  const byHole = new Map();
  for (const s of shots) {
    const n = s.hole_number;
    if (!byHole.has(n)) byHole.set(n, []);
    byHole.get(n).push(s);
  }
  for (const arr of byHole.values()) arr.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  // Aggregate per club — only shots with a valid distance count towards
  // averages (first-shot-on-hole with no tee lat/lng is null-distance).
  const perClub = new Map();
  for (const s of shots) {
    const d = typeof s.distance_y === "number" ? s.distance_y : null;
    const bucket = perClub.get(s.club) || { count: 0, distanced: [], max: 0 };
    bucket.count += 1;
    if (d !== null) {
      bucket.distanced.push(d);
      if (d > bucket.max) bucket.max = d;
    }
    perClub.set(s.club, bucket);
  }

  const sortedClubs = Array.from(perClub.entries())
    .sort((a, b) => {
      const ai = CLUB_ORDER.indexOf(a[0]);
      const bi = CLUB_ORDER.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const avg = (arr) =>
    arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;

  return (
    <div className="surface-card-flat p-4 mb-5" data-testid="shot-map-card">
      <div className="flex items-center gap-2 mb-3">
        <Golf size={20} weight="fill" className="text-[var(--accent-terracotta)]" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">Shot tracker</div>
          <div className="font-heading font-bold text-[var(--primary)]">Your shots today</div>
        </div>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatPill label="Shots logged" value={shots.length} />
        <StatPill label="Holes tracked" value={byHole.size} />
        <StatPill
          label="Longest shot"
          value={
            (() => {
              const withDist = shots.filter((s) => typeof s.distance_y === "number");
              if (!withDist.length) return "—";
              const longest = withDist.reduce((a, b) => (a.distance_y > b.distance_y ? a : b));
              return `${Math.round(longest.distance_y)}y`;
            })()
          }
        />
      </div>

      {/* Per-club table */}
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mb-1">
          Your bag today
        </div>
        <div className="rounded-xl overflow-hidden border border-[var(--border-default)]">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider bg-[var(--secondary)] text-[var(--primary)]">
              <tr>
                <th className="text-left px-3 py-1.5">Club</th>
                <th className="text-right px-3 py-1.5">Shots</th>
                <th className="text-right px-3 py-1.5">Avg</th>
                <th className="text-right px-3 py-1.5">Longest</th>
              </tr>
            </thead>
            <tbody>
              {sortedClubs.map(([club, b], idx) => {
                const a = avg(b.distanced);
                return (
                  <tr
                    key={club}
                    data-testid={`shot-club-row-${club}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-[var(--bg-app)]"}
                  >
                    <td className="text-left px-3 py-1.5 font-bold text-[var(--primary)]">
                      {CLUB_LABELS[club] || club}
                    </td>
                    <td className="text-right px-3 py-1.5 font-mono-num">{b.count}</td>
                    <td className="text-right px-3 py-1.5 font-mono-num">{a !== null ? `${a}y` : "—"}</td>
                    <td className="text-right px-3 py-1.5 font-mono-num">
                      {b.max > 0 ? `${Math.round(b.max)}y` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-hole list — collapsed to a compact one-liner per hole */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mb-1">
          By hole
        </div>
        <div className="space-y-1">
          {Array.from(byHole.keys()).sort((a, b) => a - b).map((holeNum) => {
            const holeShots = byHole.get(holeNum);
            return (
              <div
                key={holeNum}
                className="flex items-center gap-2 p-2 rounded-lg bg-white border border-[var(--border-default)]"
                data-testid={`shot-hole-row-${holeNum}`}
              >
                <div className="w-8 h-8 rounded-full bg-[var(--accent-sand)] flex items-center justify-center font-mono-num font-black text-sm text-[var(--primary)]">
                  {holeNum}
                </div>
                <div className="flex-1 flex flex-wrap gap-1">
                  {holeShots.map((s, i) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono-num font-bold bg-[var(--secondary)] text-[var(--primary)]"
                    >
                      <span className="opacity-60">{i + 1}.</span>
                      <span>{(CLUB_LABELS[s.club] || s.club).replace("-iron", "i")}</span>
                      {typeof s.distance_y === "number" && (
                        <span className="opacity-60">· {Math.round(s.distance_y)}y</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[10px] text-[var(--text-secondary)] mt-3 flex items-center gap-1">
        <TrendUp size={11} weight="bold" />
        Play a few more rounds and these averages update your Bag IQ suggestions.
      </p>
    </div>
  );
}

const StatPill = ({ label, value }) => (
  <div className="rounded-xl bg-[var(--accent-sand)] px-2 py-2 text-center">
    <div className="text-[9px] uppercase tracking-widest text-[var(--text-secondary)]">{label}</div>
    <div className="font-mono-num font-black text-lg text-[var(--primary)] leading-none mt-0.5">{value}</div>
  </div>
);
